import * as Three from 'three';

// Gaussian splat renderer for the converter's point clouds (utils/convert3d: room-<scene>-splat / -splatout /
// -splatback; COLOR_0 = rgb + a log-coded radius in alpha, geometry.splat_radius_code). Our own small renderer
// on three r124, after Kerbl et al. 2023 but isotropic: every splat is a camera-facing quad with a Gaussian
// falloff, blended back to front. The back-to-front order is computed in a worker (counting sort on view
// depth) whenever the camera has moved, and handed back as re-ordered buffers.
export const R_MIN = 0.002;
export const R_MAX = 0.4;
export const PART = { splat: 0, splatout: 1, splatback: 2, splatfill: 3 };   // splatfill: AI fill (complete.py)
const SIGMAS = 2.5;            // a quad covers ±2.5 σ
const SORT_MS = 60;            // at most one re-sort in flight; re-sort after the camera moved this long ago

export function decodeRadius(byte) {   // must match geometry.splat_radius_code
  return R_MIN * (R_MAX / R_MIN) ** (byte / 255);
}

// Back-to-front order of n splats for a view matrix (column-major, three's Matrix4.elements): counting sort on
// view-space z, farthest (most negative z) first. Pure function; also the body of the sort worker.
export function depthOrder(pos, n, m) {
  const depth = new Float32Array(n);
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < n; i += 1) {
    const z = m[2] * pos[3 * i] + m[6] * pos[3 * i + 1] + m[10] * pos[3 * i + 2] + m[14];
    depth[i] = z;
    if (z < min) min = z;
    if (z > max) max = z;
  }
  const buckets = 65536;
  const scale = (buckets - 1) / ((max - min) || 1);
  const counts = new Uint32Array(buckets);
  const keys = new Uint16Array(n);
  for (let i = 0; i < n; i += 1) {
    const k = ((depth[i] - min) * scale) | 0;
    keys[i] = k;
    counts[k] += 1;
  }
  const starts = new Uint32Array(buckets);
  for (let k = 1; k < buckets; k += 1) starts[k] = starts[k - 1] + counts[k - 1];
  const order = new Uint32Array(n);
  for (let i = 0; i < n; i += 1) {
    order[starts[keys[i]]] = i;
    starts[keys[i]] += 1;
  }
  return order;
}

const WORKER = `
const depthOrder = ${depthOrder.toString()};
let pos, col, meta, n;
onmessage = (e) => {
  const d = e.data;
  if (d.init) { pos = d.pos; col = d.col; meta = d.meta; n = d.n; return; }
  const order = depthOrder(pos, n, d.view);
  const P = new Float32Array(n * 3), C = new Uint8Array(n * 4), M = new Float32Array(n * 2);
  for (let j = 0; j < n; j += 1) {
    const i = order[j];
    P[3 * j] = pos[3 * i]; P[3 * j + 1] = pos[3 * i + 1]; P[3 * j + 2] = pos[3 * i + 2];
    C[4 * j] = col[4 * i]; C[4 * j + 1] = col[4 * i + 1]; C[4 * j + 2] = col[4 * i + 2]; C[4 * j + 3] = col[4 * i + 3];
    M[2 * j] = meta[2 * i]; M[2 * j + 1] = meta[2 * i + 1];
  }
  postMessage({ id: d.id, P, C, M }, [P.buffer, C.buffer, M.buffer]);
};`;

const VERTEX = `
attribute vec3 aCenter;
attribute vec4 aColor;
attribute vec2 aMeta;          // room index, part (0 own, 1 beyond its walls, 2 painted hidden area, 3 AI fill)
uniform float uNear;           // room whose parts 1 and 2 are shown (-1: none)
uniform float uReveal;         // intro wave 0..1 (1 = all in place)
uniform vec3 uOrigin;
uniform float uMaxDist;
uniform vec3 uTint;            // AI-painted parts (2, 3) shown in this colour when "AI areas" is on
uniform float uTintOn;
varying vec3 vColor;
varying vec2 vLocal;
varying float vAlpha;
void main() {
  if (aMeta.y > 0.5 && abs(aMeta.x - uNear) > 0.5) { gl_Position = vec4(0.0, 0.0, 2.0, 1.0); return; }
  float radius = ${R_MIN.toFixed(4)} * pow(${(R_MAX / R_MIN).toFixed(1)}, aColor.a);
  float front = uReveal * (uMaxDist + 1.5);
  float k = clamp((front - distance(aCenter, uOrigin)) / 1.5, 0.0, 1.0);
  float e = k * k * (3.0 - 2.0 * k);
  vAlpha = e;
  vColor = aMeta.y > 1.5 && uTintOn > 0.5 ? mix(aColor.rgb, uTint, 0.55) : aColor.rgb;
  vLocal = position.xy * ${SIGMAS.toFixed(1)};
  vec4 view = viewMatrix * vec4(aCenter, 1.0);
  view.xy += position.xy * radius * ${SIGMAS.toFixed(1)} * max(e, 0.15);
  gl_Position = projectionMatrix * view;
}`;

const FRAGMENT = `
uniform float uOpacity;
varying vec3 vColor;
varying vec2 vLocal;
varying float vAlpha;
void main() {
  float a = exp(-0.5 * dot(vLocal, vLocal)) * vAlpha * uOpacity;
  if (a < 0.004) discard;
  gl_FragColor = vec4(vColor, a);
}`;

export default class SplatLayer {
  constructor() {
    this.scene = new Three.Scene();
    this._parts = [];
    this.count = 0;
  }

  // a GLTF Points node of one room part; roomIndex: position of the room in the viewer's room list
  add(points, roomIndex, part) {
    const pos = points.geometry.attributes.position;
    const col = points.geometry.attributes.color;
    if (!pos || !col || col.itemSize !== 4) return;
    points.updateWorldMatrix(true, false);
    this._parts.push({ pos, col, matrix: points.matrixWorld.clone(), roomIndex, part });
    this.count += pos.count;
  }

  build() {
    const n = this.count;
    const pos = new Float32Array(n * 3);
    const col = new Uint8Array(n * 4);
    const meta = new Float32Array(n * 2);
    const v = new Three.Vector3();
    let o = 0;
    this._parts.forEach(({ pos: p, col: c, matrix, roomIndex, part }) => {
      // three r124's getX returns the stored value: bytes for the converter's uint8 COLOR_0, 0..1 if floats
      const scale = c.array instanceof Float32Array ? 255 : 1;
      for (let i = 0; i < p.count; i += 1, o += 1) {
        v.set(p.getX(i), p.getY(i), p.getZ(i)).applyMatrix4(matrix);
        pos[o * 3] = v.x; pos[o * 3 + 1] = v.y; pos[o * 3 + 2] = v.z;
        col[o * 4] = Math.round(c.getX(i) * scale);
        col[o * 4 + 1] = Math.round(c.getY(i) * scale);
        col[o * 4 + 2] = Math.round(c.getZ(i) * scale);
        col[o * 4 + 3] = Math.round(c.getW(i) * scale);
        meta[o * 2] = roomIndex; meta[o * 2 + 1] = part;
      }
    });
    this._parts = [];
    const geometry = new Three.InstancedBufferGeometry();
    geometry.setAttribute('position', new Three.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    this._center = new Three.InstancedBufferAttribute(pos, 3);
    this._color = new Three.InstancedBufferAttribute(col, 4, true);
    this._meta = new Three.InstancedBufferAttribute(meta, 2);
    [this._center, this._color, this._meta].forEach(a => a.setUsage(Three.DynamicDrawUsage));
    geometry.setAttribute('aCenter', this._center);
    geometry.setAttribute('aColor', this._color);
    geometry.setAttribute('aMeta', this._meta);
    geometry.instanceCount = n;
    this.material = new Three.ShaderMaterial({
      uniforms: {
        uNear: { value: -1 }, uReveal: { value: 1 }, uOrigin: { value: new Three.Vector3() },
        uMaxDist: { value: 10 }, uOpacity: { value: 1 }, uTint: { value: new Three.Color() }, uTintOn: { value: 0 },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthWrite: false,
    });
    this.mesh = new Three.Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
    this._worker = new Worker(URL.createObjectURL(new Blob([WORKER], { type: 'application/javascript' })));
    this._worker.postMessage({ init: true, pos: pos.slice(), col: col.slice(), meta: meta.slice(), n });
    this._worker.onmessage = (e) => this._sorted(e.data);
    this._pending = false;
    this._lastView = null;
  }

  // show room `near`'s parts beyond its walls and its painted areas (-1: only every room's own part)
  setNear(index) { if (this.material) this.material.uniforms.uNear.value = index; }

  setReveal(progress, origin, maxDist) {
    if (!this.material) return;
    const u = this.material.uniforms;
    u.uReveal.value = progress;
    if (origin) u.uOrigin.value.copy(origin);
    if (maxDist) u.uMaxDist.value = maxDist;
  }

  // tint the AI-painted splats (hidden areas behind objects, AI fill) in `color`, or show them as painted (null)
  setTint(color) {
    if (!this.material) return;
    this.material.uniforms.uTintOn.value = color == null ? 0 : 1;
    if (color != null) this.material.uniforms.uTint.value.set(color);
  }

  setOpacity(a) { if (this.material) this.material.uniforms.uOpacity.value = a; }

  // ask the worker for a new back-to-front order when the camera moved (one request in flight)
  sort(camera) {
    if (!this._worker || this._pending) return;
    camera.updateMatrixWorld();
    const view = camera.matrixWorldInverse.elements;
    if (this._lastView && view.every((x, i) => Math.abs(x - this._lastView[i]) < 1e-4)) return;
    if (this._lastSort && performance.now() - this._lastSort < SORT_MS) return;
    this._lastView = view.slice();
    this._lastSort = performance.now();
    this._pending = true;
    this._worker.postMessage({ id: this._lastSort, view: this._lastView });
  }

  _sorted({ P, C, M }) {
    this._pending = false;
    if (!this.mesh) return;
    this._center.array = P; this._center.needsUpdate = true;
    this._color.array = C; this._color.needsUpdate = true;
    this._meta.array = M; this._meta.needsUpdate = true;
  }

  dispose() {
    if (this._worker) this._worker.terminate();
    this._worker = null;
    if (this.mesh) {
      this.mesh.geometry.dispose();
      this.material.dispose();
    }
    this.mesh = null;
    this.material = null;
  }
}
